import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Hero } from './components/Hero';
import { Features } from './components/Features';
import { Personas } from './components/Personas';
import { CTA } from './components/CTA';
import { FAQSection } from './components/FAQSection';
import { JsonLd } from './components/JsonLd';
import { SeoHead } from './seo/SeoHead';
import {
  faqPageSchema,
  organizationSchema,
  softwareApplicationSchema,
  websiteSchema
} from './seo/schema';
import { SITE_URL } from './seo/config';

const homeFaqs = [
  {
    question: 'Who is StrataPro designed for?',
    answer:
      'StrataPro is built for duplexes, triplexes, and smaller strata schemes with around 2-12 lots where owners want clear systems without enterprise-level overhead.'
  },
  {
    question: 'Can self-managed strata committees use StrataPro?',
    answer:
      'Yes. The platform supports owner-led workflows for notices, records, communication, budgeting, and task tracking so committees can stay organised and compliant.'
  },
  {
    question: 'Does StrataPro replace legal or accounting advice?',
    answer:
      'No. StrataPro provides operational tools and record-keeping workflows. Committees should still seek independent legal, insurance, tax, or accounting advice when needed.'
  },
  {
    question: 'How are savings estimates presented?',
    answer:
      'Savings examples compare software subscription costs against common outsourced management fee ranges. Actual costs vary by building, location, and service scope.'
  }
];

export function App() {
  return (
    <Layout>
      <SeoHead
        title="Strata management software for duplexes and small strata"
        description="StrataPro helps small strata schemes manage meetings, compliance, records, and communication with practical software for 2-12 lot buildings."
        path="/"
      />
      <JsonLd data={[organizationSchema(), websiteSchema(), softwareApplicationSchema(SITE_URL, 'Strata management software for small owner-led strata buildings.')]} />
      <JsonLd data={faqPageSchema(homeFaqs)} />

      <Hero />
      <Features />
      <Personas />

      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900">Definition: small strata management software</h2>
          <p className="mt-4 text-gray-700 leading-relaxed">
            Small strata management software is a digital operating system for owner-led committees in low-lot buildings.
            It centralises notices, records, budgets, issues, meeting agendas, and decisions so committees can run predictable,
            auditable processes without relying on fragmented email threads.
          </p>
          <h3 className="text-2xl font-semibold text-gray-900 mt-8">How owner-led committees usually implement it</h3>
          <ol className="mt-4 list-decimal pl-6 space-y-2 text-gray-700">
            <li>Set up your scheme profile and lot structure.</li>
            <li>Upload core records: by-laws, insurance, and compliance documents.</li>
            <li>Create annual cycles for AGM prep, budgets, and recurring maintenance.</li>
            <li>Invite owners and assign role-based permissions.</li>
            <li>Track decisions, actions, and follow-ups in one place.</li>
          </ol>
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <Link to="/self-managed-strata-software" className="text-blue-700 font-medium hover:underline">Explore self-managed strata software workflows</Link>
            <Link to="/strata-agm-guide" className="text-blue-700 font-medium hover:underline">Read the practical strata AGM guide</Link>
            <Link to="/duplex-strata-management" className="text-blue-700 font-medium hover:underline">See duplex strata management guidance</Link>
            <Link to="/strata-management-cost-comparison" className="text-blue-700 font-medium hover:underline">Compare strata management cost models</Link>
            <Link to="/compliance/nsw-strata" className="text-blue-700 font-medium hover:underline">Access state compliance hubs (NSW/VIC/QLD)</Link>
            <Link to="/resources/pr-hub" className="text-blue-700 font-medium hover:underline">Browse PR resources and linkable assets</Link>
          </div>
        </div>
      </section>

      <section className="py-14 bg-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900">Trust and transparency</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-5 border">
              <h3 className="font-semibold text-gray-900">Savings methodology</h3>
              <p className="mt-2 text-gray-600 text-sm">Any savings references are directional examples based on publicly common management fee ranges and software subscription pricing by lot count.</p>
            </div>
            <div className="bg-white rounded-lg p-5 border">
              <h3 className="font-semibold text-gray-900">Privacy and security</h3>
              <p className="mt-2 text-gray-600 text-sm">StrataPro uses role-based access, audit-friendly record handling, and standard web security controls. Committees retain control over participant access.</p>
            </div>
            <div className="bg-white rounded-lg p-5 border">
              <h3 className="font-semibold text-gray-900">Company contact</h3>
              <p className="mt-2 text-gray-600 text-sm">For product, privacy, or commercial enquiries contact hello@stratapro.com.au. We provide software and implementation guidance, not legal advice.</p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection
        title="Frequently asked questions"
        subtitle="Clear answers for small strata committees evaluating software options."
        faqs={homeFaqs}
      />

      <CTA />
    </Layout>
  );
}
